import { useState, useEffect, useCallback, useMemo } from 'react'
import { X, Sparkles } from 'lucide-react'
import { apiClient } from '@/services/apiClient'
import { useNodeStore, useFiberStore } from '@/stores'
import type { AnyNode } from '@/types/nodes'
import type { Cable } from '@/types/fibers'
import {
  extractCablesSuggested,
  type AiCableSuggestion,
  type NetworkAssistantStructured,
} from '@/types/networkAssistant'
import { resolveNodeLabel, nodesForManualPick } from '@/utils/resolveNodeLabel'
import { createCableBetweenNodes } from '@/utils/createCableBetweenNodes'

interface NetworkAssistantModalProps {
  isOpen: boolean
  onClose: () => void
}

type EndpointHint = 'ok' | 'ambiguous' | 'unresolved'

interface PreviewRowState {
  rowKey: string
  suggestion: AiCableSuggestion
  fromNodeId: string
  toNodeId: string
  fromChoices: AnyNode[]
  toChoices: AnyNode[]
  fromHint: EndpointHint
  toHint: EndpointHint
  selected: boolean
  applyError: string | null
}

function nodeLabel(n: AnyNode) {
  const name = n.nombre?.trim()
  return name ? `${name} (${n.tipo})` : `${n.tipo} · ${n.id.slice(0, 8)}…`
}

function defaultNumFibras(s: AiCableSuggestion): number {
  const n = s.fibers
  if (typeof n === 'number' && Number.isFinite(n) && n >= 1) {
    return Math.min(288, Math.round(n))
  }
  return 12
}

/** Nombres que la IA debe usar en from/to (coinciden con resolveNodeLabel). */
function buildNodeLabelsForAiContext(nodes: AnyNode[]): string[] {
  const labels = nodes.map((n) => {
    const name = n.nombre?.trim()
    if (name) return name
    return `[${n.tipo}] ${n.id.slice(0, 8)}`
  })
  return [...new Set(labels)].sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }))
}

function buildCableLabelsForAiContext(cables: Cable[]): string[] {
  const labels = cables.map((c) => {
    const raw = c.nombre?.trim() || c.codigo?.trim()
    if (raw) return raw
    return `Cable ${c.id.slice(0, 8)}`
  })
  return [...new Set(labels)].sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }))
}

function cableExistsBetween(cables: Cable[], a: string, b: string): boolean {
  if (!a || !b || a === b) return false
  return cables.some(
    (c) =>
      (c.origen === a && c.destino === b) || (c.origen === b && c.destino === a)
  )
}

function buildInitialPreviewRows(suggestions: AiCableSuggestion[], nodes: AnyNode[]): PreviewRowState[] {
  const manual = nodesForManualPick(nodes)
  return suggestions.map((suggestion, index) => {
    const fromR = resolveNodeLabel(suggestion.from, nodes)
    const toR = resolveNodeLabel(suggestion.to, nodes)

    let fromNodeId: string
    let fromHint: EndpointHint
    if (fromR.kind === 'one') {
      fromNodeId = fromR.node.id
      fromHint = 'ok'
    } else if (fromR.kind === 'many') {
      fromNodeId = fromR.candidates[0]?.id ?? ''
      fromHint = 'ambiguous'
    } else {
      fromNodeId = ''
      fromHint = 'unresolved'
    }

    let toNodeId: string
    let toHint: EndpointHint
    if (toR.kind === 'one') {
      toNodeId = toR.node.id
      toHint = 'ok'
    } else if (toR.kind === 'many') {
      toNodeId = toR.candidates[0]?.id ?? ''
      toHint = 'ambiguous'
    } else {
      toNodeId = ''
      toHint = 'unresolved'
    }

    // Siempre listar todos los nodos en los desplegables (el usuario puede corregir el match).
    const fromChoices = manual
    const toChoices = manual

    return {
      rowKey: `ai-cable-${index}-${suggestion.from}-${suggestion.to}`,
      suggestion,
      fromNodeId,
      toNodeId,
      fromChoices,
      toChoices,
      fromHint,
      toHint,
      selected: fromHint !== 'unresolved' && toHint !== 'unresolved',
      applyError: null,
    }
  })
}

export function NetworkAssistantModal({ isOpen, onClose }: NetworkAssistantModalProps) {
  const [prompt, setPrompt] = useState('')
  const [loading, setLoading] = useState(false)
  const [resultText, setResultText] = useState('')
  const [structured, setStructured] = useState<NetworkAssistantStructured | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [previewRows, setPreviewRows] = useState<PreviewRowState[]>([])
  const [applying, setApplying] = useState(false)

  const nodes = useNodeStore((s) => s.nodes)
  const cables = useFiberStore((s) => s.cables)
  const addCable = useFiberStore((s) => s.addCable)
  const updateCable = useFiberStore((s) => s.updateCable)
  const loadCables = useFiberStore((s) => s.loadCables)

  const aiContext = useMemo(
    () => ({
      nodeLabels: buildNodeLabelsForAiContext(nodes),
      cableLabels: buildCableLabelsForAiContext(cables),
    }),
    [nodes, cables]
  )

  useEffect(() => {
    if (!structured) {
      setPreviewRows([])
      return
    }
    const list = extractCablesSuggested(structured)
    if (list.length === 0) {
      setPreviewRows([])
      return
    }
    setPreviewRows(buildInitialPreviewRows(list, nodes))
  }, [structured, nodes])

  const updateRow = useCallback((rowKey: string, patch: Partial<PreviewRowState>) => {
    setPreviewRows((rows) => rows.map((r) => (r.rowKey === rowKey ? { ...r, ...patch } : r)))
  }, [])

  const run = async () => {
    setError(null)
    setLoading(true)
    setResultText('')
    setStructured(null)
    setPreviewRows([])
    try {
      const res = await apiClient.post<{ structured: unknown; raw: string }>('/api/ai/network-assistant', {
        prompt,
        context: aiContext,
      })
      const obj = res.structured
      if (obj && typeof obj === 'object') {
        setStructured(obj as NetworkAssistantStructured)
        setResultText(JSON.stringify(obj, null, 2) || res.raw)
      } else {
        setStructured(null)
        setResultText(res.raw || '')
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error')
    } finally {
      setLoading(false)
    }
  }

  const applySelected = async () => {
    const toCreate = previewRows.filter((r) => r.selected)
    if (toCreate.length === 0) return

    setApplying(true)
    setPreviewRows((rows) => rows.map((r) => ({ ...r, applyError: null })))

    const pairSeen = new Set<string>()
    const cableSnapshot: Cable[] = [...cables]

    const pairKey = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`)

    for (const row of toCreate) {
      if (!row.fromNodeId || !row.toNodeId) {
        updateRow(row.rowKey, { applyError: 'Selecciona origen y destino' })
        continue
      }
      if (row.fromNodeId === row.toNodeId) {
        updateRow(row.rowKey, { applyError: 'Origen y destino no pueden ser el mismo nodo' })
        continue
      }
      const pk = pairKey(row.fromNodeId, row.toNodeId)
      if (pairSeen.has(pk) || cableExistsBetween(cableSnapshot, row.fromNodeId, row.toNodeId)) {
        updateRow(row.rowKey, { applyError: 'Ya existe un cable entre estos nodos' })
        continue
      }

      try {
        await createCableBetweenNodes(
          {
            origenId: row.fromNodeId,
            destinoId: row.toNodeId,
            nodes,
            numeroFibras: defaultNumFibras(row.suggestion),
            tipo: row.suggestion.tipo,
            observaciones: row.suggestion.notes,
          },
          addCable,
          updateCable
        )
        pairSeen.add(pk)
        cableSnapshot.push({
          id: '__pending__',
          origen: row.fromNodeId,
          destino: row.toNodeId,
        } as Cable)
      } catch (e) {
        updateRow(row.rowKey, {
          applyError: e instanceof Error ? e.message : 'Error al crear el cable',
        })
      }
    }

    try {
      await loadCables()
    } catch {
      /* store ya refleja add/update; recarga es best-effort */
    }
    setApplying(false)
  }

  const suggestions = structured ? extractCablesSuggested(structured) : []
  const selectedCount = previewRows.filter((r) => r.selected).length

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[2100] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-dark-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-200 dark:border-dark-600">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-dark-600 shrink-0">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-500" />
            Asistente de red
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 flex-1 overflow-y-auto space-y-3 text-sm">
          <p className="text-gray-600 dark:text-gray-400 text-xs">
            Describe el diseño; la IA devuelve JSON con sugerencias. Revisa la vista previa y confirma antes de crear
            cables.
          </p>
          <div
            className="text-xs rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50/80 dark:bg-amber-950/30 text-amber-950 dark:text-amber-100/90 px-3 py-2 space-y-1.5"
            role="note"
          >
            <p className="font-medium">Importante: extremos = nodos del mapa</p>
            <p>
              Solo aparecen <strong>NAP, POP, CIERRE, OLT, POSTE y CLIENTE</strong>. Los <strong>splitters</strong> viven
              dentro de la configuración de una NAP o CIERRE, no como punto en el mapa. Un <strong>cable</strong> en la
              app une <em>dos nodos</em>; no se elige un cable como origen o destino.
            </p>
            <p>
              Pide tramos entre <strong>nombres de nodos</strong> como en el explorador (p. ej. «NAP Centro» → «NAP
              Norte»). Detalle de splitters, buffers o hilos: en el texto o en el campo <code className="text-[11px]">notes</code> del JSON.
            </p>
          </div>
          <textarea
            className="w-full border rounded-lg px-3 py-2 dark:bg-dark-700 min-h-[100px]"
            placeholder="Ej.: Cable de 24 fibras desde NAP Centro hasta NAP Servicio Norte; en NAP Centro hay splitter 1x8 (detalle en notes)…"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
          {error && <p className="text-red-600 text-xs">{error}</p>}
          {resultText && (
            <pre className="text-xs bg-gray-100 dark:bg-dark-900 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap max-h-48">
              {resultText}
            </pre>
          )}

          {suggestions.length > 0 && previewRows.length > 0 && (
            <div className="space-y-2 border-t border-gray-200 dark:border-dark-600 pt-3">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                Vista previa de cables sugeridos
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Origen y destino deben ser nodos del mapa. Ajusta los desplegables si la IA usó una etiqueta que no es
                nombre de nodo. Solo se crearán las filas marcadas.
              </p>
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-dark-600">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-dark-900">
                    <tr>
                      <th className="p-2 text-left w-8"></th>
                      <th className="p-2 text-left">Desde (IA)</th>
                      <th className="p-2 text-left">Origen</th>
                      <th className="p-2 text-left">Hasta (IA)</th>
                      <th className="p-2 text-left">Destino</th>
                      <th className="p-2 text-left">Fibras</th>
                      <th className="p-2 text-left">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row) => {
                      const dup =
                        row.fromNodeId &&
                        row.toNodeId &&
                        cableExistsBetween(cables, row.fromNodeId, row.toNodeId)
                      return (
                        <tr
                          key={row.rowKey}
                          className="border-t border-gray-100 dark:border-dark-700 align-top"
                        >
                          <td className="p-2">
                            <input
                              type="checkbox"
                              checked={row.selected}
                              onChange={(e) => updateRow(row.rowKey, { selected: e.target.checked })}
                            />
                          </td>
                          <td className="p-2 text-gray-600 dark:text-gray-400">{row.suggestion.from}</td>
                          <td className="p-2">
                            <select
                              className="w-full max-w-[200px] border rounded px-1 py-0.5 dark:bg-dark-700"
                              value={row.fromNodeId}
                              onChange={(e) => updateRow(row.rowKey, { fromNodeId: e.target.value })}
                            >
                              {row.fromChoices.length === 0 && (
                                <option value="">— Sin nodos —</option>
                              )}
                              {row.fromHint === 'unresolved' && (
                                <option value="">Elegir nodo…</option>
                              )}
                              {row.fromChoices.map((n) => (
                                <option key={n.id} value={n.id}>
                                  {nodeLabel(n)}
                                </option>
                              ))}
                            </select>
                            {row.fromHint === 'ambiguous' && (
                              <p className="text-amber-600 dark:text-amber-400 mt-0.5">Varias coincidencias</p>
                            )}
                            {row.fromHint === 'unresolved' && (
                              <p className="text-amber-600 dark:text-amber-400 mt-0.5">Sin coincidencia por nombre</p>
                            )}
                          </td>
                          <td className="p-2 text-gray-600 dark:text-gray-400">{row.suggestion.to}</td>
                          <td className="p-2">
                            <select
                              className="w-full max-w-[200px] border rounded px-1 py-0.5 dark:bg-dark-700"
                              value={row.toNodeId}
                              onChange={(e) => updateRow(row.rowKey, { toNodeId: e.target.value })}
                            >
                              {row.toChoices.length === 0 && (
                                <option value="">— Sin nodos —</option>
                              )}
                              {row.toHint === 'unresolved' && (
                                <option value="">Elegir nodo…</option>
                              )}
                              {row.toChoices.map((n) => (
                                <option key={n.id} value={n.id}>
                                  {nodeLabel(n)}
                                </option>
                              ))}
                            </select>
                            {row.toHint === 'ambiguous' && (
                              <p className="text-amber-600 dark:text-amber-400 mt-0.5">Varias coincidencias</p>
                            )}
                            {row.toHint === 'unresolved' && (
                              <p className="text-amber-600 dark:text-amber-400 mt-0.5">Sin coincidencia por nombre</p>
                            )}
                          </td>
                          <td className="p-2">{defaultNumFibras(row.suggestion)}</td>
                          <td className="p-2">
                            {dup && (
                              <span className="text-amber-600 dark:text-amber-400 block">Cable existente</span>
                            )}
                            {row.applyError && (
                              <span className="text-red-600 dark:text-red-400 block">{row.applyError}</span>
                            )}
                            {!dup && !row.applyError && row.selected && (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        <div className="p-4 border-t border-gray-200 dark:border-dark-600 flex flex-wrap justify-end gap-2 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-dark-600"
          >
            Cerrar
          </button>
          {suggestions.length > 0 && previewRows.length > 0 && (
            <button
              type="button"
              disabled={applying || selectedCount === 0}
              onClick={() => void applySelected()}
              className="px-4 py-2 rounded-lg bg-emerald-600 text-white disabled:opacity-50"
            >
              {applying ? 'Creando…' : `Crear cables seleccionados (${selectedCount})`}
            </button>
          )}
          <button
            type="button"
            disabled={loading || prompt.length < 5}
            onClick={() => void run()}
            className="px-4 py-2 rounded-lg bg-violet-600 text-white disabled:opacity-50"
          >
            {loading ? 'Pensando…' : 'Consultar'}
          </button>
        </div>
      </div>
    </div>
  )
}
